import { IS_PT_LANGUAGE } from "./language";
import { RECURRING_PRICE_LOOKUP_KEY } from "./constants";
import { isNativeIOS } from "utils";

export const groupByType = (products) => {
  return products?.reduce((previous, current) => {
    previous[current.type] = previous[current.type] || [];
    previous[current.type].push(current);
    return previous;
  }, Object.create(null));
};

const isSellable = (price) => price?.active !== false;

export const getRecurringPriceLookupKey = () =>
  IS_PT_LANGUAGE
    ? RECURRING_PRICE_LOOKUP_KEY.brl
    : RECURRING_PRICE_LOOKUP_KEY.usd;

/**
 * Picks the monthly subscription price by its Stripe lookup_key.
 * Returns undefined while the prices have not loaded, or if the lookup_key is
 * missing on Stripe - the caller must render a loading state, never a
 * hardcoded price.
 */
export const getRecurringProduct = (prices) => {
  const lookupKey = getRecurringPriceLookupKey();

  return prices?.find(
    (price) => isSellable(price) && price.lookup_key === lookupKey
  );
};

/**
 * One-time prices have no lookup_key yet, so they are still picked by currency.
 * Sorted by id so the choice does not depend on the order Stripe returns.
 */
export const getOneTimeProduct = (prices) => {
  const currency = IS_PT_LANGUAGE ? "brl" : "usd";

  return prices
    ?.filter((price) => isSellable(price) && price.currency === currency)
    .sort((a, b) => `${a.id}`.localeCompare(`${b.id}`))[0];
};

export const getAndroidProduct = (callback) => {
  if (!("getDigitalGoodsService" in window)) {
    return;
  }
  window
    .getDigitalGoodsService("https://play.google.com/billing")
    .then((service) => {
      if (!service) {
        callback(null);
        return;
      }
      service
        .getDetails(["bibleplan_subscription_0"])
        .then((itemDetails) => {
          const item = itemDetails?.[0];
          callback(
            item
              ? { ...item, digitalGoodsAPIAvailable: true }
              : null
          );
        })
        .catch(() => {
          callback(null);
        });
    })
    .catch(() => {
      callback(null);
    });
};

export const getProductTitle = (product) => {
  return IS_PT_LANGUAGE
    ? product?.product.metadata["BR-title"]
    : product?.product.metadata["EN-title"];
};

export const getLocalePrice = (unit) => {
  const locale = IS_PT_LANGUAGE ? "pt-BR" : "en-US";
  const currency = IS_PT_LANGUAGE ? "BRL" : "USD";

  const localePrice = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(unit / 100);

  return localePrice;
};

export const toPrice = ({ cents, currency }) => {
  const locale = currency === "brl" ? "pt-BR" : "en-US";

  if (!cents || !currency) return "";

  const localePrice = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(cents / 100);

  return localePrice;
};

export const getProductPrice = (product) => {
  if (isNativeIOS) return product?.price;

  // using stripe
  return product?.unit_amount && getLocalePrice(product?.unit_amount);
};

export const getIOSProductPrice = (product) => {
  return product?.price;
};

export const getAndroidProductPrice = (product) => {
  if (!product?.price?.currency || product?.price?.value == null) {
    return "";
  }
  return new Intl.NumberFormat(navigator.language, {
    style: "currency",
    currency: product.price.currency,
  }).format(product.price.value);
};

export const getStripeProductPrice = (product) => {
  return product?.unit_amount && getLocalePrice(product?.unit_amount);
};
